import { Platform } from "@angular/cdk/platform";
import { inject, Injectable, signal } from "@angular/core";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { PwaInstallPromptComponent } from "./pwa-install-prompt.component";

interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

@Injectable({
	providedIn: "root",
})
export class PwaService {
	private bottomSheet = inject(MatBottomSheet);
	private platform = inject(Platform);

	public promptEvent = signal<BeforeInstallPromptEvent | null>(null);
	public mobileType = signal<"android" | "ios" | undefined>(undefined);
	public canBeInstalled = signal(false);
	public isInstalled = signal(false);

	initPwaPrompt() {
		if (this.platform.ANDROID || this.platform.BLINK) {
			this.mobileType.set("android");
			// cSpell:ignore beforeinstallprompt
			window.addEventListener("beforeinstallprompt", (event: any) => {
				event.preventDefault();
				this.promptEvent.set(event as BeforeInstallPromptEvent);
				this.canBeInstalled.set(true);
			});
		} else if (this.platform.IOS || this.platform.SAFARI) {
			this.mobileType.set("ios");
			const installed = !!(
				"standalone" in window.navigator && window.navigator["standalone"]
			);
			this.isInstalled.set(installed);
			this.canBeInstalled.set(!installed);
		}
	}

	isInPwaMode(): boolean {
		// Check for iOS PWA mode
		const pwaOnIOS = !!(
			"standalone" in window.navigator && window.navigator["standalone"]
		);

		// Check for PWA mode on Android
		const pwaModes = ["standalone", "fullscreen", "minimal-ui"];
		const pwaOnAndroid = pwaModes.some(
			(mode) => window.matchMedia(`(display-mode: ${mode})`).matches,
		);

		return pwaOnIOS || pwaOnAndroid;
	}

	openPromptComponent() {
		this.bottomSheet.open(PwaInstallPromptComponent, {
			data: { mobileType: this.mobileType(), promptEvent: this.promptEvent() },
		});
	}
}
